import { StatusTag } from "@/views/components/StatusTag";
import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Input, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Page } from "@zhihaoscm/entity";
import type { InstallPromotionRecord } from "@zhihaoscm/entity";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Dialog as ZhDialog } from "zhihao-ui-react";
import {
    Table as ZhTable,
    type TableColumnProps,
    Form as ZhFilterForm,
    Button as ZhFilterButton,
    Input as ZhFilterInput,
    Select as ZhFilterSelect
} from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { INSTALL_PROMOTION_REOCRD_STATE } from "@/meta/logistics/installPromotionReocrd";
import { useUserStore } from "@/stores/react/authStore";
import { notifyOperationSuccess } from "@/utils/operationFeedback";

export interface InstallPromotionRecordQuery {
    page: number;
    size: number;
    code?: string;
    promoter?: string;
    shipName?: string;
    state?: number;
    isAsc?: number;
}

export interface InstallPromotionRecordPageProps {
    loadRecords?: (query: InstallPromotionRecordQuery) => Promise<Page<InstallPromotionRecord>>;
    revokeRecord?: (id: number | string, revocationReason: string) => Promise<unknown>;
    permissions?: string[];
}

// 对齐旧 Vue ProTable 的分页默认值，并确保后续翻页不改变接口 page size。
const INSTALL_PROMOTION_RECORD_PAGE_SIZE = 10;
const defaultQuery: InstallPromotionRecordQuery = { page: 1, size: INSTALL_PROMOTION_RECORD_PAGE_SIZE };

/** 复用 Vue 安装推广记录分页接口。 */
export async function loadInstallPromotionRecords(
    query: InstallPromotionRecordQuery
): Promise<Page<InstallPromotionRecord>> {
    const response = await request.service.get<Page<InstallPromotionRecord>>("/install/promotion/record/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 安装推广记录列表撤销接口。 */
export async function revokeInstallPromotionRecordFromList(id: number | string, revocationReason: string) {
    const response = await request.service.get(`/install/promotion/record/revoke/activation/${id}`, {
        params: { revocationReason }
    });
    return response.data;
}

const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);
const formatCustomer = (customer?: InstallPromotionRecord["promoter"]) =>
    customer
        ? `${customer.institutionName || customer.realName || DEFAULT_VALUE} | ${customer.mobile || DEFAULT_VALUE} | ${customer.code || DEFAULT_VALUE}`
        : DEFAULT_VALUE;

/** 物流管理-安装推广记录列表，覆盖 Vue 首屏筛选、分页、字段和状态文案。 */
export function InstallPromotionRecordPage({
    loadRecords = loadInstallPromotionRecords,
    revokeRecord = revokeInstallPromotionRecordFromList,
    permissions
}: InstallPromotionRecordPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 保持 Vue v-auth 语义：显式测试权限优先，否则读取当前用户权限。
    const canWrite = (permissions ?? storePermissions).includes("ROLE_DEVICE_RECORD_W");
    const [rows, setRows] = useState<InstallPromotionRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<InstallPromotionRecordQuery>(defaultQuery);
    const [code, setCode] = useState("");
    const [promoter, setPromoter] = useState("");
    const [shipName, setShipName] = useState("");
    const [state, setState] = useState<number | undefined>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [revokeRow, setRevokeRow] = useState<InstallPromotionRecord>();
    const [revokeReason, setRevokeReason] = useState("");
    const [revokeError, setRevokeError] = useState("");
    const [revoking, setRevoking] = useState(false);

    useEffect(() => {
        let disposed = false;
        void loadRecords(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("安装推广记录加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadRecords, query]);

    // 将 Vue ProTable 的草稿筛选值转换为分页请求，并归一到第一页。
    const submitFilters = () => {
        const nextQuery: InstallPromotionRecordQuery = { page: 1, size: INSTALL_PROMOTION_RECORD_PAGE_SIZE };
        const trimmedCode = code.trim();
        const trimmedPromoter = promoter.trim();
        const trimmedShipName = shipName.trim();
        if (trimmedCode) nextQuery.code = trimmedCode;
        if (trimmedPromoter) nextQuery.promoter = trimmedPromoter;
        if (trimmedShipName) nextQuery.shipName = trimmedShipName;
        if (state !== undefined) nextQuery.state = state;
        if (query.isAsc !== undefined) nextQuery.isAsc = query.isAsc;
        setQuery(nextQuery);
    };

    const resetFilters = () => {
        setCode("");
        setPromoter("");
        setShipName("");
        setState(undefined);
        setQuery(defaultQuery);
    };

    const confirmRevoke = async () => {
        const reason = revokeReason.trim();
        if (!revokeRow?.id) return;
        if (!reason) {
            setRevokeError("请输入撤销理由");
            return;
        }
        setRevoking(true);
        try {
            await revokeRecord(revokeRow.id, reason);
            notifyOperationSuccess("已撤销激活");
            setRevokeRow(undefined);
            setRevokeReason("");
            setRevokeError("");
            const result = await loadRecords(query);
            setRows(result.list || []);
            setTotal(result.total || 0);
        } catch {
            // 保留撤销弹窗和用户输入的理由，反馈 Vue 操作失败语义并避免未处理 rejection。
            setRevokeError("安装推广记录撤销失败");
        } finally {
            setRevoking(false);
        }
    };

    // 保留 Vue ProTable 的创建时间排序参数，zhihao-ui 的方向映射为旧接口 isAsc 值。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "createdTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) delete nextQuery.isAsc;
                else nextQuery.isAsc = order === "ascending" ? 1 : 0;
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue 安装推广记录 ProTable 字段和操作插槽。
    const columns = useMemo<TableColumnProps<InstallPromotionRecord>[]>(
        () => [
            {
                key: "code",
                label: "编号",
                minWidth: 148,
                prop: "code",
                render: ({ row }) => (
                    <Link to={`/logistics/installPromotionRecord/info/${row.id}`}>{row.code || DEFAULT_VALUE}</Link>
                )
            },
            {
                key: "promoter",
                label: "推介人账号",
                minWidth: 204,
                prop: "promoter",
                render: ({ row }) => formatCustomer(row.promoter)
            },
            {
                key: "ship",
                label: "船舶",
                minWidth: 204,
                prop: "ship",
                render: ({ row }) => row.ship?.cnname || row.ship?.name || DEFAULT_VALUE
            },
            {
                key: "createdTime",
                label: "创建时间",
                width: 188,
                prop: "createdTime",
                sortable: true,
                render: ({ row }) => formatDate(row.createdTime)
            },
            {
                key: "state",
                label: "状态",
                width: 134,
                prop: "state",
                render: ({ row }) => (
                    <StatusTag>{INSTALL_PROMOTION_REOCRD_STATE.getIntroByCode(row.state) || DEFAULT_VALUE}</StatusTag>
                )
            },
            {
                key: "operation",
                label: "操作",
                width: 87,
                prop: "operation",
                fixed: "right",
                render: ({ row }) =>
                    canWrite && Number(row.state) === INSTALL_PROMOTION_REOCRD_STATE.ACTIVATED.code ? (
                        <Button
                            aria-label={`撤销-${row.code || row.id}`}
                            disabled={banButton}
                            type="link"
                            onClick={() => {
                                setRevokeRow(row);
                                setRevokeReason("");
                                setRevokeError("");
                            }}
                        >
                            撤销
                        </Button>
                    ) : null
            }
        ],
        [banButton, canWrite]
    );

    return (
        <section aria-label="install-promotion-record-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<InstallPromotionRecord>
                    rowKey="id"
                    data={rows}
                    columns={normalizeOperationColumns(columns, rows)}
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Space className="mb-4" wrap>
                                <Typography.Title className="mb-0 mt-0" level={4}>
                                    安装推广记录
                                </Typography.Title>
                                {canWrite ? (
                                    <Link to="/logistics/installPromotionRecord/add">
                                        <Button disabled={banButton}>新增</Button>
                                    </Link>
                                ) : null}
                            </Space>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <ZhFilterForm
                                className="admin-table-filter-form mb-4"
                                layout="inline"
                                onFinish={submitFilters}
                            >
                                <ZhFilterForm.Item label="编号">
                                    <ZhFilterInput
                                        aria-label="编号"
                                        maxLength={32}
                                        placeholder="请输入编号搜索"
                                        value={code}
                                        onChange={event => setCode(event.target.value)}
                                    />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item label="推介人账号">
                                    <ZhFilterInput
                                        aria-label="推介人账号"
                                        placeholder="请输入账号信息搜索"
                                        value={promoter}
                                        onChange={event => setPromoter(event.target.value)}
                                    />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item label="船舶名称">
                                    <ZhFilterInput
                                        aria-label="船舶名称"
                                        placeholder="请输入船舶名称搜索"
                                        value={shipName}
                                        onChange={event => setShipName(event.target.value)}
                                    />
                                </ZhFilterForm.Item>
                                <ZhFilterForm.Item label="状态">
                                    <ZhFilterSelect
                                        aria-label="状态"
                                        allowClear
                                        placeholder="请选择状态"
                                        value={state}
                                        options={INSTALL_PROMOTION_REOCRD_STATE.options.map(option => ({
                                            label: option.label,
                                            value: Number(option.value)
                                        }))}
                                        onChange={setState}
                                    />
                                </ZhFilterForm.Item>
                                <Space>
                                    <ZhFilterButton aria-label="查询" htmlType="submit" type="primary">
                                        查询
                                    </ZhFilterButton>
                                    <ZhFilterButton onClick={resetFilters}>重置</ZhFilterButton>
                                </Space>
                            </ZhFilterForm>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={INSTALL_PROMOTION_RECORD_PAGE_SIZE}
                                total={total}
                                hideOnSinglePage
                                onChange={page =>
                                    setQuery(current => ({
                                        ...current,
                                        page,
                                        size: INSTALL_PROMOTION_RECORD_PAGE_SIZE
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
            <ZhDialog
                title="撤销激活"
                open={Boolean(revokeRow)}
                width={544}
                onCancel={() => setRevokeRow(undefined)}
                footer={
                    <Space>
                        <Button onClick={() => setRevokeRow(undefined)}>取消</Button>
                        <Button
                            aria-label="确定"
                            loading={revoking}
                            type="primary"
                            onClick={() => void confirmRevoke()}
                        >
                            确定
                        </Button>
                    </Space>
                }
            >
                <Alert className="mb-4" message="是否撤销激活，撤销后此安装推广将失效" type="info" />
                <Input.TextArea
                    maxLength={200}
                    showCount
                    placeholder="请输入撤销理由"
                    value={revokeReason}
                    onChange={event => setRevokeReason(event.target.value)}
                />
                {revokeError ? <Typography.Text type="danger">{revokeError}</Typography.Text> : null}
            </ZhDialog>
        </section>
    );
}
